import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import {
  KIND_LABELS,
  isKind,
  frequencyPatch,
  parseNotificationPreferences,
  thresholdPatch,
  toggleDisabled,
  togglePatch,
  type NotificationKind,
  type NotificationPreferences,
  type NotificationPreferencesPatch,
  type SummaryFrequency,
} from '../../api/notificationPreferences'
import { emit } from '../../platform/analytics/analytics'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { useToast } from '../../platform/feedback/ToastProvider'

/**
 * Settings › Notifications — the read and the three writers.
 *
 * **Every writer is `networkMode: 'always'`.** TanStack's default pauses a
 * mutation forever while offline, so a toggle would sit on "saving" with nothing
 * to tell the user; with `'always'` the request fails at once and rolls back.
 *
 * **All three share one `scope`**, so saves run one at a time, in click order.
 * `disabled_types` REPLACES the server's list, so a toggle's patch is built when
 * it RUNS from a fresh GET of the server's list plus this one change — never from
 * the cache, which in a long-open tab can be hours old. Serial order means every
 * earlier toggle in this tab is already confirmed on the server when the GET
 * happens, so two quick toggles cannot overwrite each other. A window of one
 * round trip remains (another device changing the list between our GET and
 * PATCH): the endpoint has no version check, so it cannot close to zero.
 *
 * **Rollback restores only the field a mutation changed** (one kind's switch, the
 * frequency), never a whole snapshot, so another in-flight change survives it.
 *
 * The cache converges on the server with one invalidation, issued by whichever
 * writer settles LAST: refetching while a later save is queued would overwrite
 * its optimistic change with a stale list.
 */
const SAVE_ERROR = 'Could not save the setting.'

/**
 * The failure toast lives on the hook, not on a `mutate(…, { onError })` call: per-call callbacks belong to the
 * observer's LATEST call, so a second quick toggle would silence the first one's failure.
 */
const failed = (what: string) => `We couldn’t save that. ${what} is unchanged.`

function withKind(prefs: NotificationPreferences, kind: NotificationKind, enabled: boolean): NotificationPreferences {
  const disabledWire = toggleDisabled(prefs.disabledWire, kind, enabled)
  return { ...prefs, disabledWire, disabled: disabledWire.filter(isKind), types: prefs.types.map((t) => (t.kind === kind ? { ...t, enabled } : t)) }
}

async function patch(body: NotificationPreferencesPatch) {
  const { data, error } = await api.PATCH('/api/v1/users/me/notification-preferences', { body })
  if (error !== undefined || data === undefined) throw new Error(SAVE_ERROR)
}

const settle = (client: QueryClient) => {
  // This writer is itself still counted while `onSettled` runs.
  if (client.isMutating({ mutationKey: keys.notificationPreferences.write() }) > 1) return
  return client.invalidateQueries({ queryKey: keys.notificationPreferences.all() })
}

const base = { networkMode: 'always', mutationKey: keys.notificationPreferences.write(), scope: { id: 'notification-preferences' } } as const

export function useNotificationPreferencesState(): AsyncState<NotificationPreferences> {
  const query = useQuery({
    queryKey: keys.notificationPreferences.all(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/users/me/notification-preferences')
      if (error !== undefined || data === undefined) throw new Error('Could not load notification preferences.')
      return parseNotificationPreferences(data)
    },
  })
  return fromQuery(query, {
    message: () => 'We couldn’t load your notification settings. Check your connection and try again.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

export function useSetNotificationKind() {
  const client = useQueryClient()
  const { show } = useToast()
  const key = keys.notificationPreferences.all()
  return useMutation<void, Error, { kind: NotificationKind; enabled: boolean }, { was: boolean | undefined }>({
    ...base,
    mutationFn: async ({ kind, enabled }) => {
      const { data, error } = await api.GET('/api/v1/users/me/notification-preferences')
      if (error !== undefined || data === undefined) throw new Error(SAVE_ERROR)
      await patch(togglePatch(parseNotificationPreferences(data).disabledWire, kind, enabled))
    },
    onMutate: async ({ kind, enabled }) => {
      await client.cancelQueries({ queryKey: key })
      const was = client.getQueryData<NotificationPreferences>(key)?.types.find((t) => t.kind === kind)?.enabled
      client.setQueryData<NotificationPreferences>(key, (cur) => cur && withKind(cur, kind, enabled))
      return { was }
    },
    onError: (_e, { kind }, context) => {
      show({ kind: 'error', message: failed(KIND_LABELS[kind].label) })
      const was = context?.was
      if (was !== undefined) client.setQueryData<NotificationPreferences>(key, (cur) => cur && withKind(cur, kind, was))
    },
    onSuccess: (_d, { kind, enabled }) =>
      emit('notification_preference_changed', { notification_kind: kind, notification_enabled: enabled }),
    onSettled: () => settle(client),
  })
}

export function useSetSummaryFrequency() {
  const client = useQueryClient()
  const { show } = useToast()
  const key = keys.notificationPreferences.all()
  return useMutation<void, Error, SummaryFrequency, { was: SummaryFrequency | undefined }>({
    ...base,
    mutationFn: (next) => patch(frequencyPatch(next)),
    onMutate: async (next) => {
      await client.cancelQueries({ queryKey: key })
      const was = client.getQueryData<NotificationPreferences>(key)?.spendingSummaryFrequency
      client.setQueryData<NotificationPreferences>(key, (cur) => cur && { ...cur, spendingSummaryFrequency: next })
      return { was }
    },
    onError: (_e, _next, context) => {
      show({ kind: 'error', message: failed('The frequency') })
      const was = context?.was
      if (was !== undefined)
        client.setQueryData<NotificationPreferences>(key, (cur) => cur && { ...cur, spendingSummaryFrequency: was })
    },
    onSuccess: (_d, next) => emit('notification_frequency_changed', { frequency_kind: next }),
    onSettled: () => settle(client),
  })
}

/** Not optimistic: the field shows what the user typed until the server confirms, and rejects so it can say so. */
export function useSetLargeTransactionThreshold() {
  const client = useQueryClient()
  const key = keys.notificationPreferences.all()
  return useMutation<void, Error, number, { before: number | undefined }>({
    ...base,
    mutationFn: (cents) => patch(thresholdPatch(cents)),
    onMutate: () => ({ before: client.getQueryData<NotificationPreferences>(key)?.largeTransactionThresholdCents }),
    onSuccess: (_d, cents, context) => {
      client.setQueryData<NotificationPreferences>(key, (cur) => cur && { ...cur, largeTransactionThresholdCents: cents })
      const before = context?.before
      if (before !== undefined && before !== cents) emit('notification_threshold_changed', { direction: cents > before ? 'up' : 'down' })
    },
    onSettled: () => settle(client),
  })
}
